import React from "react";
import * as PropTypes from "prop-types";
import { Col, Row } from "antd";
import { QueryBuilder } from "@cubejs-client/react";
import { ChartRenderer } from "./ChartRenderer";
import MemberGroup from "./QueryBuilder/MemberGroup";
import DraggedMemberGroup from "./QueryBuilder/DraggedMemberGroup";
import FilterGroup from "./QueryBuilder/FilterGroup";
import DynamicFilterGroup from "./QueryBuilder/DynamicFilterGroup";

import TimeGroup from "./QueryBuilder/TimeGroup";

import SelectChartType from "./QueryBuilder/SelectChartType";
import Settings from "./components/Settings/Settings";
import { Card, SectionHeader, SectionRow } from "./components";
import styled from "styled-components";
import Surface from "../../components/layouts/Surface";
import { useIntl } from "react-intl";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const Section = styled.div`
  display: flex;
  flex-flow: column;
  margin-right: 24px;
  margin-bottom: 16px;

  > *:first-child {
    margin-bottom: 8px;
  }
`;

const playgroundActionUpdateMethods = (updateMethods, memberName) =>
  Object.keys(updateMethods)
    .map((method) => ({
      [method]: (member, values, ...rest) => {
        let actionName = `${method
          .split("")
          .map((c, i) => (i === 0 ? c.toUpperCase() : c))
          .join("")} Member`;
        if (values && values.values) {
          actionName = "Update Filter Values";
        }
        if (values && values.dateRange) {
          actionName = "Update Date Range";
        }
        if (values && values.granularity) {
          actionName = "Update Granularity";
        }
        return updateMethods[method].apply(null, [member, values, ...rest]);
      },
    }))
    .reduce((a, b) => ({ ...a, ...b }), {});

export default function PlaygroundQueryBuilder({
  query,
  cubejsApi,
  apiUrl,
  cubejsToken,
  dashboardSource,
  setQuery,
  onVizStateChanged,
  visState,
}) {
  const intl = useIntl();
  return (
    <QueryBuilder
      query={query}
      setQuery={setQuery}
      defaultChartType={"table"}
      disableHeuristics={true}
      cubejsApi={cubejsApi}
      wrapWithQueryRenderer={false}
      initialVizState={visState}
      onVizStateChanged={onVizStateChanged}
      render={({
        resultSet,
        error,
        isQueryPresent,
        chartType,
        updateChartType,
        measures,
        availableMeasures,
        updateMeasures,
        dimensions,
        availableDimensions,
        updateDimensions,
        segments,
        availableSegments,
        updateSegments,
        filters,
        updateFilters,
        timeDimensions,
        availableTimeDimensions,
        updateTimeDimensions,
        orderMembers,
        updateOrder,
        pivotConfig,
        updatePivotConfig,
      }) => {
        let parsedDateRange = null;
        if (resultSet) {
          const { timeDimensions = [] } =
            resultSet.pivotQuery() || resultSet.query() || {};
          parsedDateRange = timeDimensions[0]?.dateRange;
        }
        const showSegmentFilter = true;
        return (
          <DndProvider backend={HTML5Backend}>
            <Row
              justify="space-around"
              align="top"
              gutter={24}
              style={{ marginBottom: 12, marginRight: 0, marginLeft: 0 }}
            >
              <Col span={24}>
                <Card bordered={false} style={{ borderRadius: 0 }}>
                  <Row
                    justify="stretch"
                    align="top"
                    gutter={0}
                    style={{ marginBottom: -12 }}
                  >
                    <Section>
                      <SectionHeader>
                        {intl.formatMessage({
                          id: "app.reports.measures",
                        })}
                      </SectionHeader>
                      <MemberGroup
                        members={measures}
                        availableMembers={availableMeasures}
                        addMemberName={intl.formatMessage({
                          id: "app.reports.measures",
                        })}
                        updateMethods={playgroundActionUpdateMethods(
                          updateMeasures,
                          "Measure"
                        )}
                        intl={intl}
                      />
                    </Section>
                    <Section>
                      <SectionHeader>
                        {intl.formatMessage({
                          id: "app.reports.dimensions",
                        })}
                      </SectionHeader>
                      <DraggedMemberGroup
                        members={dimensions}
                        availableMembers={availableDimensions}
                        addMemberName={intl.formatMessage({
                          id: "app.reports.dimensions",
                        })}
                        updateMethods={playgroundActionUpdateMethods(
                          updateDimensions,
                          "Dimension"
                        )}
                        intl={intl}
                        pivotConfig={pivotConfig}
                        updatePivotConfig={updatePivotConfig}
                      />
                    </Section>
                    {showSegmentFilter ? (
                      <Section>
                        <SectionHeader>
                          {intl.formatMessage({
                            id: "app.reports.segment",
                          })}
                        </SectionHeader>
                        <MemberGroup
                          members={segments}
                          availableMembers={availableSegments}
                          addMemberName={intl.formatMessage({
                            id: "app.reports.segment",
                          })}
                          updateMethods={playgroundActionUpdateMethods(
                            updateSegments,
                            "Segment"
                          )}
                          intl={intl}
                        />
                      </Section>
                    ) : null}
                    <Section>
                      <SectionHeader>
                        {intl.formatMessage({
                          id: "app.reports.time",
                        })}
                      </SectionHeader>
                      <TimeGroup
                        members={timeDimensions}
                        availableMembers={availableTimeDimensions}
                        addMemberName={intl.formatMessage({
                          id: "app.reports.time",
                        })}
                        updateMethods={playgroundActionUpdateMethods(
                          updateTimeDimensions,
                          "Time"
                        )}
                        parsedDateRange={parsedDateRange}
                        intl={intl}
                      />
                    </Section>

                    <Section>
                      <SectionHeader>
                        {intl.formatMessage({
                          id: "app.reports.filters",
                        })}
                      </SectionHeader>
                      <FilterGroup
                        members={filters.map((filter) => ({
                          ...filter,
                          operators:
                            filter.dimension.type === "time"
                              ? [
                                  ...filter.operators,
                                  { name: "set", title: "is set" },
                                  { name: "notSet", title: "is not set" },
                                ]
                              : filter.operators,
                        }))}
                        availableMembers={availableDimensions.concat(
                          availableMeasures
                        )}
                        addMemberName={intl.formatMessage({
                          id: "app.reports.filters",
                        })}
                        updateMethods={playgroundActionUpdateMethods(
                          updateFilters,
                          "Filter"
                        )}
                        intl={intl}
                      />
                    </Section>

                    <Section>
                      <SectionHeader>
                        {intl.formatMessage({
                          id: "app.reports.dynamicFilters",
                        })}
                      </SectionHeader>
                      <DynamicFilterGroup
                        availableMembers={availableDimensions.concat(
                          availableMeasures
                        )}
                        addMemberName={intl.formatMessage({
                          id: "app.reports.dynamicFilters",
                        })}
                        intl={intl}
                      />
                    </Section>
                  </Row>
                </Card>

                <SectionRow
                  style={{ marginTop: 16, marginLeft: 16, padding: 20 }}
                >
                  <SelectChartType
                    chartType={chartType}
                    updateChartType={(type) => {
                      updateChartType(type);
                    }}
                    intl={intl}
                  />

                  <Settings
                    isQueryPresent={isQueryPresent}
                    limit={query.limit}
                    pivotConfig={pivotConfig}
                    orderMembers={orderMembers}
                    onReorder={updateOrder.reorder}
                    onOrderChange={updateOrder.set}
                    onMove={updatePivotConfig.moveItem}
                    onUpdate={updatePivotConfig.update}
                    intl={intl}
                  />
                </SectionRow>
              </Col>
            </Row>

            <Row
              justify="space-around"
              align="top"
              gutter={24}
              style={{ marginRight: 0, marginLeft: 0, height: " 100%" }}
            >
              <Col
                span={24}
                style={{ padding: 0, height: "100%", background: "#f3f6f8" }}
              >
                <Surface>
                  {isQueryPresent ? (
                    <ChartRenderer
                      query={query}
                      resultSet={resultSet}
                      error={error}
                      chartType={chartType}
                      cubejsApi={cubejsApi}
                      pivotConfig={pivotConfig}
                    />
                  ) : (
                    <h2 style={{ textAlign: "center" }}>
                      {intl.formatMessage({
                        id: "app.reports.chooseMeasureOrDimension",
                      })}
                    </h2>
                  )}
                </Surface>
              </Col>
            </Row>
          </DndProvider>
        );
      }}
    />
  );
}

PlaygroundQueryBuilder.propTypes = {
  query: PropTypes.object,
  setQuery: PropTypes.func,
  cubejsApi: PropTypes.object,
  dashboardSource: PropTypes.object,
  apiUrl: PropTypes.string,
  cubejsToken: PropTypes.string,
};

PlaygroundQueryBuilder.defaultProps = {
  query: {},
  setQuery: null,
  cubejsApi: null,
  dashboardSource: null,
  apiUrl: "/cubejs-api/v1",
  cubejsToken: null,
};
